/**
 * Layout principal autenticado (TH-53153 + TH-54204 + TH-54205).
 *
 * Sidebar fixa em desktop (>= md) e drawer em mobile. Sidebar segue o
 * protótipo: header com mark $ + wordmark `price$how`, items agrupados em
 * OPERAÇÃO / CONTA, footer com avatar do usuário. Sem contadores fixos e sem
 * item que leve a página provisória (Telas e Ajustes são PageStub) — PSHWO-67349.
 *
 * Topbar (`<Topbar />`) consome props `breadcrumb`/`title`/`actions`
 * passadas por cada Page. As `actions` da página entram ao lado do
 * `<UserMenu />` (que carrega o logout) — nunca no lugar dele (PSHWO-66720).
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Store,
  Package,
  FileImage,
  Upload,
  Map,
  Users,
  X,
  Building2,
  Tag,
  Activity,
  ImageIcon,
  ListChecks,
  Megaphone,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { TenantChip } from "@/components/TenantChip";
import { Topbar } from "@/components/Topbar";
import { UserMenu } from "@/components/UserMenu";
import { WhatsNewModal, useWhatsNew } from "@/components/WhatsNewModal";
import { useAuth, type CurrentUser } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  superAdminOnly?: boolean;
  /** Restringe a um conjunto de roles. Vazio/ausente = todos veem. */
  visibleTo?: readonly string[];
  count?: string;
}

interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Operação",
    items: [
      { label: "Visão geral", to: "/", icon: LayoutDashboard },
      { label: "Clientes", to: "/clients", icon: Building2, superAdminOnly: true },
      { label: "Lojas", to: "/stores", icon: Store },
      { label: "Áreas", to: "/areas", icon: Tag },
      { label: "Campanhas", to: "/campaigns", icon: Megaphone },
      { label: "Produtos", to: "/products", icon: Package },
      { label: "Templates", to: "/templates", icon: FileImage },
      { label: "Artes", to: "/custom-arts", icon: ImageIcon },
      { label: "Importações", to: "/imports", icon: Upload },
      { label: "Mapeamentos", to: "/import-mappings", icon: Map },
      {
        label: "Catálogo",
        to: "/catalog",
        icon: ImageIcon,
        visibleTo: ["super_admin", "catalog_operator"],
      },
      {
        label: "Fila do catálogo",
        to: "/catalog/queue",
        icon: ListChecks,
        visibleTo: ["super_admin", "catalog_operator"],
      },
      {
        label: "Revisão do stage",
        to: "/catalog/stage",
        icon: ListChecks,
        superAdminOnly: true,
      },
      {
        label: "Fila de IA",
        to: "/admin/queue",
        icon: Activity,
        superAdminOnly: true,
      },
      {
        label: "Curadoria de artes",
        to: "/admin/arts/review",
        icon: ImageIcon,
        visibleTo: ["super_admin", "catalog_operator"],
      },
    ],
  },
  {
    label: "Conta",
    items: [
      { label: "Usuários", to: "/users", icon: Users },
    ],
  },
];

interface AppLayoutProps {
  children: ReactNode;
  /** Breadcrumb da topbar (mono uppercase), ex.: `OPERAÇÃO / LOJAS`. */
  breadcrumb?: string;
  /** Título grande da topbar. Default: "Priceshow" (fallback de segurança). */
  title?: string;
  /** Ações da página no slot direito da topbar, antes do UserMenu (sempre presente). */
  actions?: ReactNode;
}

export function AppLayout({
  children,
  breadcrumb,
  title,
  actions,
}: AppLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useAuth();

  // Modal "Novidades" pós-upgrade. Abre automaticamente quando a versão
  // do build é diferente da última vista pelo user (localStorage). Pode
  // ser reaberto manualmente clicando na versão do footer.
  const { shouldShow: shouldShowWhatsNew } = useWhatsNew();
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  useEffect(() => {
    if (shouldShowWhatsNew && user) setWhatsNewOpen(true);
  }, [shouldShowWhatsNew, user]);

  // O `??` daqui trocava o UserMenu pelas actions da página: 21 das 33 páginas
  // passam `actions` (inclusive o Dashboard, que é a home), e nelas o menu de
  // usuário — e portanto o logout — simplesmente sumia. As actions são
  // ADICIONAIS ao menu, nunca substitutas dele.
  const topbarActions = (
    <>
      <TenantChip />
      {actions}
      <UserMenu />
    </>
  );

  return (
    <div className="flex min-h-screen bg-[var(--color-background)] text-[var(--color-foreground)]">
      {/* Sidebar — desktop */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-card)] md:flex">
        <SidebarContent
          user={user}
          onOpenWhatsNew={() => setWhatsNewOpen(true)}
        />
      </aside>

      {/* Drawer — mobile */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setDrawerOpen(false)}
          role="presentation"
        >
          <aside
            className="absolute inset-y-0 left-0 flex w-60 flex-col border-r border-[var(--color-border)] bg-[var(--color-card)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end p-2">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Fechar menu"
                onClick={() => setDrawerOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SidebarContent
              user={user}
              onNavigate={() => setDrawerOpen(false)}
              onOpenWhatsNew={() => {
                setDrawerOpen(false);
                setWhatsNewOpen(true);
              }}
            />
          </aside>
        </div>
      ) : null}

      <div className="flex flex-1 flex-col">
        <Topbar
          breadcrumb={breadcrumb}
          title={title ?? "Priceshow"}
          actions={topbarActions}
          onMenuClick={() => setDrawerOpen(true)}
        />

        {/* Padding 28px fiel ao protótipo (p-7 = 28px) */}
        <main className="flex-1 overflow-y-auto p-7">{children}</main>
      </div>

      <WhatsNewModal open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />
    </div>
  );
}

function SidebarContent({
  user,
  onNavigate,
  onOpenWhatsNew,
}: {
  user: CurrentUser | null;
  onNavigate?: () => void;
  onOpenWhatsNew?: () => void;
}) {
  const role = user?.role;
  return (
    <>
      {/* Header: mark $ + wordmark price$how — sem borda inferior, segue protótipo */}
      <div className="flex items-center gap-2.5 px-4 pb-[14px] pt-5">
        <Link
          to="/"
          onClick={onNavigate}
          aria-label="Priceshow — início"
          className="flex items-center gap-2.5"
        >
          <span
            aria-hidden
            className="flex h-7 w-7 items-center justify-center rounded-md bg-[var(--color-background)] font-mono text-base font-bold text-[var(--color-primary)]"
          >
            $
          </span>
          <span className="text-[15px] font-semibold tracking-tight text-[var(--color-foreground)]">
            price<span className="font-mono text-[var(--color-primary)]">$</span>how
          </span>
        </Link>
      </div>

      {/* Grupos de navegação */}
      <nav className="flex-1 overflow-y-auto px-2">
        {NAV_GROUPS.map((group) => {
          const visible = group.items.filter((item) => {
            if (item.superAdminOnly && role !== "super_admin") return false;
            if (item.visibleTo && (!role || !item.visibleTo.includes(role))) {
              return false;
            }
            return true;
          });
          if (visible.length === 0) return null;
          return (
            <div key={group.label} className="flex flex-col gap-0.5">
              <div className="px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">
                {group.label}
              </div>
              {visible.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-[10px] rounded-md px-[10px] py-2 text-[13px] font-medium",
                    "text-[var(--color-muted-foreground)] transition-colors",
                    "hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)]",
                  )}
                  activeProps={{
                    className: cn(
                      "flex items-center gap-[10px] rounded-md px-[10px] py-2 text-[13px] font-medium",
                      "bg-[var(--color-primary-soft)] text-[var(--color-primary)]",
                    ),
                  }}
                >
                  <item.icon className="h-[15px] w-[15px] shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {item.count ? (
                    <span className="font-mono text-[11px] text-[var(--color-muted-foreground)]">
                      {item.count}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Footer: avatar + nome + tenant */}
      <SidebarFooter user={user} onOpenWhatsNew={onOpenWhatsNew} />
    </>
  );
}

interface SidebarFooterProps {
  user: CurrentUser | null;
  onOpenWhatsNew?: () => void;
}

function SidebarFooter({ user, onOpenWhatsNew }: SidebarFooterProps) {
  const initials = getInitials(user);
  const displayName = user?.name ?? user?.email ?? "";
  const subtitle = user
    ? user.tenant_id
      ? `Tenant · ${user.tenant_id.slice(0, 8)}`
      : user.role === "super_admin"
        ? "Super admin"
        : "—"
    : "Vila Nova · 3 lojas";
  return (
    <div className="border-t border-[var(--color-border)]">
      <div className="flex items-center gap-2.5 p-3">
        <span
          aria-hidden
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-soft-strong)] text-[12px] font-semibold text-[var(--color-primary)]"
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-medium text-[var(--color-foreground)]">
            {displayName}
          </div>
          <div className="truncate font-mono text-[10px] text-[var(--color-muted-foreground)]">
            {subtitle}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onOpenWhatsNew}
        title="Ver novidades desta versão"
        className="w-full border-t border-[var(--color-border)] px-3 py-1.5 text-center font-mono text-[10px] tabular-nums text-[var(--color-muted-foreground)] hover:bg-[var(--color-table-row-tint)] hover:text-[var(--color-primary)]"
      >
        priceshow v{__APP_VERSION__}
      </button>
    </div>
  );
}

function getInitials(user: CurrentUser | null): string {
  if (!user) return "—";
  const source = user.name?.trim() || user.email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
